/* eslint-disable react/display-name */
import React, {
  ReactElement,
  cloneElement,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
} from "react";

import { useCombineRefs } from "../../../../ui/hooks";

const scrollIntoView = (element: HTMLElement): void => {
  const { scrollX, scrollY } = window;
  element.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "center",
  });
  window.scroll(scrollX, scrollY);
};

export interface ScrollBoxProps {
  children: ReactElement[];
  onPartSelect: (partId: string) => void;
}

export const ScrollBox = forwardRef<HTMLDivElement, ScrollBoxProps>(
  ({ children, onPartSelect }, forwardedRef) => {
    const localRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | number>(0);

    const combinedRef = useCombineRefs(localRef, forwardedRef);

    const kllTimeout = useCallback(() => {
      const { current } = timeoutRef;
      if (current) {
        clearTimeout(current);
      }
    }, []);

    useEffect(() => {
      kllTimeout();
    }, [kllTimeout]);

    const handleUpdate = (scrollBox: HTMLDivElement): void => {
      const childrenElements = Array.from(
        scrollBox.children
      ) as HTMLDivElement[];
      const scrollBoxRect = scrollBox.getBoundingClientRect();

      const cCenter = scrollBoxRect.left + scrollBoxRect.width / 2;

      const visibleElements = childrenElements
        .filter((child) => child.id)
        .sort((a, b) => {
          const ar = a.getBoundingClientRect();
          const br = b.getBoundingClientRect();
          const aCenter = ar.left + ar.width / 2;
          const bCenter = br.left + br.width / 2;

          return Math.abs(cCenter - aCenter) - Math.abs(cCenter - bCenter);
        });

      if (visibleElements.length) {
        const middleElement = visibleElements[0];
        const partId = middleElement.id.split("-")[1];
        scrollIntoView(middleElement);
        onPartSelect(partId);
      }
    };

    useEffect((): void => {
      const box = localRef.current;
      if (box) {
        handleUpdate(box);
      }
    }, []);

    return (
      <div
        onScroll={(event): void => {
          kllTimeout();
          const scrollBox = event.target as HTMLDivElement;
          timeoutRef.current = setTimeout(() => {
            handleUpdate(scrollBox);
          }, 60);
        }}
        className="combo__scrollbox"
        ref={combinedRef}
      >
        <div className="combo__tile md:combo__tile--md lg:combo__tile--lg invisible hidden md:block" />
        <div className="combo__tile md:combo__tile--md lg:combo__tile--lg invisible hidden lg:block" />
        {children.map((child, key) =>
          cloneElement(child, {
            key,
            onClick(event: MouseEvent): void {
              scrollIntoView(event.target as HTMLElement);
            },
          })
        )}
        <div className="combo__tile md:combo__tile--md lg:combo__tile--lg invisible hidden md:block" />
        <div className="combo__tile md:combo__tile--md lg:combo__tile--lg invisible hidden lg:block" />
      </div>
    );
  }
);
